import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../../../shared/lib/supabase';
import { Compass, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
export function AuthPage() {
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Listen for auth state changes to catch errors and handle successful login
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          setAuthError(null); // Clear error on successful sign in
          
          // Check if there's a pending invitation token
          const pendingToken = localStorage.getItem('pendingInvitationToken');
          if (pendingToken) {
            console.log('🎯 Found pending invitation token, redirecting to ');
            localStorage.removeItem('pendingInvitationToken');
            window.location.href = `/accept-invitation?token=${pendingToken}`;
          } else {
            // Normal login flow - redirect to dashboard
            window.location.href = '/';
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Custom Google sign-in handler with error catching
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setAuthError(null);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (error) {
        if (error.message.includes('Email not approved')) {
          setAuthError('Alpha Access - Invited Users Only');
        } else {
          setAuthError(error.message);
        }
      }
    } catch (error: any) {
      if (error.message.includes('Email not approved')) {
        setAuthError('Alpha Access - Invited Users Only');
      } else {
        setAuthError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if we're in invitation flow
  const pendingToken = localStorage.getItem('pendingInvitationToken');
  const isInvitationFlow = !!pendingToken;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-gray-50">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="glass-button rounded-sm p-3 mr-3">
              <Compass className="h-8 w-8 text-gray-700" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-600 to-gray-800 bg-clip-text text-transparent">
              Anthrogen:Sail
            </h1>
          </div>
          <p className="text-gray-600">
            Advanced protein and substrate analysis for molecular discovery
          </p>
          <div className="mt-2">
            <span className="glass-button px-3 py-1 text-xs font-medium text-gray-600 rounded-sm">
              Alpha
            </span>
          </div>
        </div>

        {/* Invitation Flow Notice */}
        {isInvitationFlow && (
          <div className="mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <Compass className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="text-gray-800 font-medium">Team Invitation Pending</p>
                <p className="text-gray-600 text-sm mt-1">
                  Please log in to accept your team invitation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Custom Error Message */}
        {authError && (
          <div className="mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-gray-600 flex-shrink-0" />
              <div>
                <p className="text-gray-800 font-medium">Alpha Access - Invited Users Only</p>
                <p className="text-gray-600 text-sm mt-1">
                  This application is currently in alpha testing. Please contact an administrator for access.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Auth Form */}
        <div className="glass-card rounded-sm p-8 shadow-xl">
          {/* Custom Google Sign-In Button */}
          <div className="mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-gradient-to-r from-gray-600 to-gray-800 text-white py-3 px-6 rounded-sm font-medium hover:from-gray-700 hover:to-gray-900 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {isInvitationFlow ? 'Sign in to Accept Invitation' : 'Sign in with Google'}
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Regular Auth Component for Email/Password */}
          <Auth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              style: {
                button: {
                  background: 'linear-gradient(45deg, #4B5563, #374151)',
                  color: 'white',
                  borderRadius: '0.75rem',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease-in-out',
                },
                input: {
                  borderRadius: '0.75rem',
                  border: '1px solid #E5E7EB',
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
                label: {
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  marginBottom: '0.5rem',
                },
                message: {
                  borderRadius: '0.75rem',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                },
                anchor: {
                  color: '#6B7280',
                  textDecoration: 'none',
                  fontWeight: '500',
                },
                divider: {
                  backgroundColor: '#E5E7EB',
                  margin: '1.5rem 0',
                },
              },
            }}
            providers={[]} // Remove Google from here since we handle it manually
            redirectTo={window.location.origin}
            view="sign_in"
            showLinks={true}
            localization={{
              variables: {
                sign_in: {
                  email_label: 'Email',
                  password_label: 'Password',
                  button_label: isInvitationFlow ? 'Sign In to Accept Invitation' : 'Sign In',
                  loading_button_label: 'Signing in...',
                  social_provider_text: 'Sign in with {{provider}}',
                  link_text: "Don't have an account? Sign up",
                },
                sign_up: {
                  email_label: 'Email',
                  password_label: 'Password',
                  button_label: isInvitationFlow ? 'Sign Up to Accept Invitation' : 'Sign Up',
                  loading_button_label: 'Signing up...',
                  social_provider_text: 'Sign up with {{provider}}',
                  link_text: 'Already have an account? Sign in',
                  confirmation_text: 'Check your email for a verification link',
                },
              },
            }}
          />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered by Odyssey™ AI
          </p>
          {isInvitationFlow && (
            <p className="text-xs text-gray-400 mt-2">
              After logging in, you'll be redirected to accept your team invitation
            </p>
          )}
        </div>
      </div>
    </div>
  );
}